"use client";

import {
  type FC,
  type Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InterestRatesOverview,
  type InterestRatesOverviewHandle,
  type InterestRatesOverviewFilterValues,
  interestRateRowImageStyle,
} from "@huub-web/shared";
import {
  Button,
  IconButton,
  useActions,
  type FormFieldSchema,
  type UIAction,
  type UINavigateAction,
} from "@huub-web/ui";
import { addParamsToURL } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import {
  useTemplates,
  useInputUpdate,
  useAgentFormAwareness,
} from "../../hooks";

export type InterestRatesTemplateProps = {
  /**
   * Unique identifier for this template instance
   */
  id: string;
  /**
   * API endpoint to fetch interest rates from
   */
  interestRatesEndpoint: string;
  /**
   * Optional bank name to filter rates
   */
  bank?: string;
  /**
   * Max number of rates to show
   */
  limit?: number;
  /**
   * Named UIActions for CTA buttons and bank clicks
   */
  actions?: {
    /**
     * CTA button on each row
     */
    cta?: UIAction;
    /**
     * CTA in the gradient card
     */
    start?: UIAction;
    /**
     * Bank click action (template appends bankName to navigation context)
     */
    bank?: UINavigateAction;
  };
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * Title for the gradient CTA card
   */
  ctaTitle?: string;
  /**
   * Description for the gradient CTA card
   */
  ctaDescription?: string;
  /**
   * Button label for the gradient CTA card
   */
  ctaButtonValue?: string;
};

const INTEREST_RATES_FIELD_SCHEMA: Omit<FormFieldSchema, "value" | "errors">[] =
  [
    {
      name: "rentevastePeriod",
      label: "Rentevaste periode (jaren)",
      type: "select",
      options: [
        { value: "5", label: "5" },
        { value: "10", label: "10" },
        { value: "20", label: "20" },
        { value: "30", label: "30" },
      ],
    },
    {
      name: "debtMarketValue",
      label: "Marktwaarde / LTV",
      type: "select",
      options: [
        { value: "nhg", label: "NHG" },
        { value: "60", label: "60%" },
        { value: "80", label: "80%" },
        { value: "90", label: "90%" },
        { value: "100", label: "100%" },
      ],
    },
    {
      name: "energyLabel",
      label: "Energielabel",
      type: "select",
      options: [
        { value: "A", label: "A" },
        { value: "B", label: "B" },
        { value: "C", label: "C" },
        { value: "D", label: "D" },
        { value: "E", label: "E" },
        { value: "F", label: "F" },
        { value: "G", label: "G" },
      ],
    },
  ];

/**
 * InterestRatesTemplate
 * A template component that wraps InterestRatesOverview with API integration and CTA actions
 */
export const InterestRatesTemplate: FC<InterestRatesTemplateProps> = ({
  id,
  interestRatesEndpoint,
  bank,
  limit,
  actions,
  ctaTitle,
  ctaDescription,
  ctaButtonValue,
  ref,
}) => {
  const { $get } = useTemplates();
  const { handleAction } = useActions();
  const { t } = useTranslation();

  const filterRef = useRef<InterestRatesOverviewHandle>(null);
  const [currentFilters, setCurrentFilters] = useState({
    rentevastePeriod: "20",
    debtMarketValue: "nhg",
    energyLabel: "A",
  });

  useInputUpdate({
    prefix: `${id}__`,
    debugLabel: "InterestRatesTemplate",
    onUpdate: useCallback((updates: Record<string, unknown>) => {
      filterRef.current?.updateFilterValues(
        updates as Partial<InterestRatesOverviewFilterValues>,
      );
    }, []),
  });

  const fieldSchemaWithValues = useMemo<FormFieldSchema[]>(
    () =>
      INTEREST_RATES_FIELD_SCHEMA.map((f) => ({
        ...f,
        value: currentFilters[f.name as keyof typeof currentFilters] ?? null,
        errors: [],
      })),
    [currentFilters],
  );

  useAgentFormAwareness({
    formId: id,
    formType: "filter",
    fieldSchema: fieldSchemaWithValues,
  });

  const handleFiltersLoaded = useCallback(
    (filters: {
      rentevastePeriod: string;
      debtMarketValue: string;
      energyLabel: string;
    }) => {
      setCurrentFilters(filters);
    },
    [],
  );

  const getInterestRates = useCallback(
    async (
      period: string,
      debtMarketValue: string,
      energyLabel: string,
      rateLimit: number,
      bankName?: string,
    ) => {
      const url = addParamsToURL(interestRatesEndpoint, {
        period,
        debtMarketValue,
        energyLabel,
        limit: String(rateLimit),
        ...(bankName ? { bank: bankName } : {}),
      });

      const response = await $get(url);
      return response?.data?.rates ?? [];
    },
    [interestRatesEndpoint, $get],
  );

  const ctaAction = actions?.cta;
  const startAction = actions?.start;
  const bankAction = actions?.bank;

  return (
    <InterestRatesOverview
      ref={ref}
      filterRef={filterRef}
      variant="app"
      getInterestRates={getInterestRates}
      onFiltersLoaded={handleFiltersLoaded}
      bank={bank}
      limit={limit}
      ctaTitle={ctaTitle}
      ctaDescription={ctaDescription}
      getBankImage={(src, name, _href, slug) => {
        const handleBankAction = bankAction
          ? () => {
              const baseContext = bankAction.payload?.context ?? [];
              handleAction({
                type: "navigate",
                payload: {
                  context: [...baseContext, slug ?? name],
                  ...(bankAction.payload?.params && {
                    params: bankAction.payload.params,
                  }),
                },
              } satisfies UIAction);
            }
          : undefined;

        if (handleBankAction) {
          return (
            <Button onClick={handleBankAction} variant="opaqueCurrentColor">
              <img
                src={src}
                alt={name}
                className={interestRateRowImageStyle}
                draggable={false}
              />
            </Button>
          );
        }

        return (
          <img
            src={src}
            alt={name}
            className={interestRateRowImageStyle}
            draggable={false}
          />
        );
      }}
      cta={
        ctaAction ? (
          <Button
            onClick={() => handleAction(ctaAction)}
            tracking="interest_rates_cta"
          >
            {t("interestRatesOverview.ctaButton")}
          </Button>
        ) : undefined
      }
      ctaSmall={
        ctaAction ? (
          <IconButton
            icon="chevronRight"
            rounded
            onClick={() => handleAction(ctaAction)}
            tracking="interest_rates_cta_small"
          />
        ) : undefined
      }
      startCTA={
        startAction ? (
          <Button
            variant="secondary"
            onClick={() => handleAction(startAction)}
            tracking="interest_rates_start_cta"
            style={{ alignSelf: "center" }}
          >
            {ctaButtonValue ?? t("interestRatesOverview.startButton")}
          </Button>
        ) : undefined
      }
    />
  );
};
